import UserProfileButton from '@efdigital/ll-xceed-v2/components/user/user-profile-button.gts';
import type { TemplateOnlyComponent } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';

export type NavItem = {
  link: {
    route: string;
    models?: unknown[];
    disabled?: boolean;
  };
  icon: string;
  label: string;
  css?: string;
};

interface SidebarContainerSignature {
  Args: {
    nav?: NavItem[];
    helpRoute?: string;
  };
  Element: HTMLElement;
  Blocks: {
    sidebar: [className: string];
    content: [];
  };
}

const EMPTY_ARRAY: unknown[] = [];

const SidebarContainer: TemplateOnlyComponent<SidebarContainerSignature> =
  <template>
    <div class="row g-0 flex-grow-1 min-h-0">
      <div class="col-lg-4 col-xl-3 d-none d-lg-block border-end">
        <aside
          class="d-flex flex-column h-100 overflow-hidden bg-primary-subtle ll-sidebar-nav sticky-top"
          aria-label="Navigation"
          ...attributes
        >
          {{#if (has-block "sidebar")}}
            <div
              class="{{if @nav 'flex-shrink-0 border-bottom' 'flex-grow-1'}}
                border-primary-subtle bg-primary-subtle"
            >
              {{yield "p-3 overflow-auto min-h-0" to="sidebar"}}
            </div>
          {{/if}}

          {{#if @nav}}
            <nav
              class="nav nav-pills flex-column gap-1 p-3 flex-grow-1 overflow-auto min-h-0"
              aria-label="Main navigation"
            >
              {{#each @nav as |item|}}
                <LinkTo
                  @route={{item.link.route}}
                  @models={{if item.link.models item.link.models EMPTY_ARRAY}}
                  @disabled={{item.link.disabled}}
                  class="nav-link d-flex align-items-center gap-3 {{item.css}}"
                >
                  <iconify-icon inline icon={{item.icon}} aria-hidden="true" />
                  {{item.label}}
                </LinkTo>
              {{/each}}
            </nav>
          {{/if}}

          <div
            class="flex-shrink-0 border-top border-primary-subtle bg-primary-subtle"
          >
            {{#if @helpRoute}}
              <div class="px-3 pt-2">
                <LinkTo
                  @route={{@helpRoute}}
                  class="nav-link d-flex align-items-center gap-3"
                >
                  <iconify-icon inline icon="ph:question" aria-hidden="true" />
                  Help
                </LinkTo>
              </div>
            {{/if}}

            <UserProfileButton />
          </div>
        </aside>
      </div>

      {{! No overflow-hidden here: the app scrolls the window, so this column
          grows to content height and clips nothing — but an overflow:hidden
          ancestor silently breaks position:sticky for descendants (e.g. the
          coach profile editor's section nav). min-w-0 still guards against
          flex children forcing horizontal overflow. }}
      <div class="col-lg-8 col-xl-9 d-flex flex-column min-w-0 min-h-0">
        {{yield to="content"}}
      </div>
    </div>

    <div
      class="offcanvas offcanvas-start d-lg-none"
      tabindex="-1"
      id="sidebarMobileMenu"
      aria-labelledby="sidebarMobileMenuLabel"
    >
      <div class="offcanvas-header border-bottom">
        <h2 class="offcanvas-title h5 mb-0" id="sidebarMobileMenuLabel">
          Menu
        </h2>
        <button
          type="button"
          class="btn-close"
          data-bs-dismiss="offcanvas"
          aria-label="Close menu"
        ></button>
      </div>
      <div class="offcanvas-body p-0">
        <nav
          class="nav nav-pills flex-column gap-1"
          aria-label="Mobile navigation"
        >
          {{#if (has-block "sidebar")}}
            {{yield "p-3 overflow-auto min-h-0" to="sidebar"}}
          {{/if}}

          {{#if @nav}}
            <div class="p-3">
              {{#each @nav as |item|}}
                <LinkTo
                  @route={{item.link.route}}
                  @models={{if item.link.models item.link.models EMPTY_ARRAY}}
                  @disabled={{item.link.disabled}}
                  class="nav-link d-flex align-items-center gap-3"
                  data-bs-dismiss="offcanvas"
                >
                  <iconify-icon
                    inline
                    icon={{item.icon}}
                    class="fs-5"
                    aria-hidden="true"
                  />
                  {{item.label}}
                </LinkTo>
              {{/each}}
            </div>
          {{/if}}

          {{#if @helpRoute}}
            <LinkTo
              @route={{@helpRoute}}
              class="nav-link d-flex align-items-center gap-3"
              data-bs-dismiss="offcanvas"
            >
              <iconify-icon
                inline
                icon="ph:question"
                class="fs-5"
                aria-hidden="true"
              />
              Help
            </LinkTo>
          {{/if}}
          <UserProfileButton />

        </nav>
      </div>
    </div>
  </template>;

export default SidebarContainer;
